import { useMemo, useState } from "react";
import { LayoutList, Pencil, Trash2, useDialog, type ItemProps } from "@repo/ui";
import { useTasksByBrand } from "~/apis/use-tasks";
import { matchesTaskFields } from "~/pages/dashboard/task-manager/task-manager-helpers";
import type { Brand } from "~/types/api";
import type { TaskManagerDTO } from "~/types/api-custom";
import { ChangeStatusDialog } from "../change-status-dialog/change-status-dialog";
import { useCustomerFields } from "../use-customer-fields";
import { useDeleteTask } from "../use-delete-task";
import { useWorkflows } from "../use-workflows";
import { BrandRowBody } from "./brand-row-body";
import { BrandRowHeader } from "./brand-row-header";
import { getActiveLabel, useTaskColumns } from "./use-task-columns";

export type TaskActionsContext = {
  row: TaskManagerDTO;
  index: number;
};

type Props = {
  brand: Brand;
  isExpanded: boolean;
  onToggle: () => void;
  onHover: () => void;
  searchQuery: string;
  showAll: boolean;
  showTaskDialog: (task?: TaskManagerDTO) => void;
};

export const BrandRow = ({
  brand,
  isExpanded,
  onToggle,
  onHover,
  searchQuery,
  showAll,
  showTaskDialog,
}: Props) => {
  const { tasks, isLoading } = useTasksByBrand(brand.id, isExpanded);
  const { workflows } = useWorkflows();
  const { handleDelete } = useDeleteTask();

  const { fields: customerFields, isLoading: isLoadingFields } = useCustomerFields(
    brand.customerId,
    isExpanded
  );

  const { showDialog, closeDialog } = useDialog();
  const [changeStatusDialogOpen, setChangeStatusDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TaskManagerDTO | null>(null);

  const dynamicColumns = useTaskColumns({
    customerFields: customerFields || [],
    workflows: workflows || [],
  });

  const actions: ItemProps<TaskActionsContext>[] = useMemo(
    () => [
      {
        id: "edit",
        label: "Edit Task",
        icon: <Pencil className="h-4 w-4" />,
        onClick: (ctx: TaskActionsContext) => showTaskDialog(ctx.row),
      },
      {
        id: "change",
        label: "Change Task Status",
        icon: <LayoutList className="h-4 w-4" />,
        onClick: (ctx: TaskActionsContext) => {
          setSelectedTask(ctx.row);
          setChangeStatusDialogOpen(true);
        },
      },
      {
        id: "delete",
        label: "Delete Task",
        icon: <Trash2 className="h-4 w-4" />,
        destructive: true,
        onClick: (ctx: TaskActionsContext) => {
          showDialog({
            title: "Confirm Delete",
            description: `Are you sure you want to delete task "${ctx.row.name}"?`,
            confirmText: "Delete task",
            confirmVariant: "destructive",
            cancelText: "Go Back",
            onConfirm: async () => {
              await handleDelete(ctx.row.id);
              closeDialog();
            },
          });
        },
      },
    ],
    [showTaskDialog, showDialog, handleDelete, closeDialog]
  );

  const workflowById = useMemo(
    () => new Map((workflows ?? []).map((workflow) => [workflow.id, workflow])),
    [workflows]
  );

  const lowerSearchQuery = searchQuery.trim().toLowerCase();

  const filteredTasks = useMemo(() => {
    return tasks
      .filter((task) => {
        const matchesSearch = lowerSearchQuery
          ? task.name.toLowerCase().includes(lowerSearchQuery) ||
            getActiveLabel(task.active).toLowerCase().includes(lowerSearchQuery) ||
            (workflowById.get(task.workflowId)?.name.toLowerCase().includes(lowerSearchQuery) ??
              false) ||
            matchesTaskFields({ task, customerFields, workflowById, searchQuery: lowerSearchQuery })
          : true;

        const matchesStatus = showAll ? true : task.active;
        return matchesSearch && matchesStatus;
      })
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  }, [tasks, lowerSearchQuery, showAll, workflowById, customerFields]);

  const activeCount = useMemo(() => tasks.filter((t) => t.active).length, [tasks]);
  const totalCount = tasks.length;

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <BrandRowHeader
        brand={brand}
        isExpanded={isExpanded}
        isLoading={isLoading}
        activeCount={activeCount}
        totalCount={totalCount}
        onToggle={onToggle}
        onHover={onHover}
      />

      {isExpanded && (
        <BrandRowBody
          isLoading={isLoading || isLoadingFields}
          tasks={filteredTasks}
          columns={dynamicColumns}
          actions={actions}
          searchQuery={searchQuery}
          showAll={showAll}
        />
      )}

      {selectedTask && (
        <ChangeStatusDialog
          task={selectedTask}
          isOpen={changeStatusDialogOpen}
          onOpenChange={setChangeStatusDialogOpen}
        />
      )}
    </div>
  );
};
